import Close16 from '@carbon/icons-react/es/close/16';
import React, { FC, useCallback } from 'react';
import styled from 'styled-components';
import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent,
} from 'components/atoms/drawer';
import { Form } from 'components/atoms/form';
import { useInviteModalActions } from 'components/organisms/module-settings/team-members/invite-modal/actions';
import { useBillingFeatureFlag } from 'hooks/use-account';
import { TeamMembersDrawerProps } from '../types';
import { TeamMemberDrawerBody } from './modal-body';
import { TeamMemberDrawerFooter } from './modal-footer';
import { TeamMemberDrawerHeader } from './modal-header';

const StyledCustomDrawerContent = styled(CustomLayoutDrawerContent)`
  ${CustomLayoutDrawerContent.Title} {
    padding: var(--popover-spacing) var(--popover-spacing) 0 var(--spacing-06);
  }

  ${CustomLayoutDrawerContent.Content} {
    padding-top: var(--spacing-06);
    padding-left: var(--spacing-06);
    padding-right: var(--spacing-06);
  }

  ${CustomLayoutDrawerContent.Footer} {
    padding: var(--spacing-06);
  }
`;

export const TeamMemberDrawerModal: FC<TeamMembersDrawerProps> = ({
  drawerState,
  onClose,
  showMemberDetail,
  changeToEditMode,
  onUserInvited,
  onInviteSubmit,
  showLoader,
  drawerSectionProps,
  permissionOptions,
  roleOptions,
  formInputRules,
  processRequestInParent,
  isVendorInvite,
}) => {
  const { readOnly, visible, selected } = drawerState;
  const [form] = Form.useForm();
  const isBillingEnabled = useBillingFeatureFlag();

  const { isSaving, onInvite, onUpdate } = useInviteModalActions({
    form,
    onClose,
    onUserInvited,
    showMemberDetail,
  });

  const _onInviteSubmit = useCallback(async () => {
    if (isBillingEnabled || processRequestInParent) {
      const formValues = await form.validateFields();

      onInviteSubmit?.(formValues);
    } else {
      onInvite();
    }
  }, [
    form,
    isBillingEnabled,
    onInvite,
    onInviteSubmit,
    processRequestInParent,
  ]);

  return (
    <CustomLayoutDrawer
      placement="right"
      visible={visible}
      width={680}
      mask
      destroyOnClose
      onClose={onClose}
      closeIcon={<Close16 />}
    >
      <StyledCustomDrawerContent>
        <CustomLayoutDrawerContent.Title>
          <TeamMemberDrawerHeader readOnly={readOnly} user={selected?.user} />
        </CustomLayoutDrawerContent.Title>
        <CustomLayoutDrawerContent.Content>
          <TeamMemberDrawerBody
            form={form}
            readOnly={readOnly}
            member={selected}
            changeToEditMode={changeToEditMode}
            sectionProps={drawerSectionProps}
            permissionOptions={permissionOptions}
            roleOptions={roleOptions}
            formInputRules={formInputRules}
            isVendorInvite={isVendorInvite}
          />
        </CustomLayoutDrawerContent.Content>

        {!readOnly && (
          <CustomLayoutDrawerContent.Footer>
            <TeamMemberDrawerFooter
              user={selected?.user}
              isSaving={isSaving || showLoader === true}
              onInvite={_onInviteSubmit}
              onUpdate={onUpdate}
              onClose={onClose}
            />
          </CustomLayoutDrawerContent.Footer>
        )}
      </StyledCustomDrawerContent>
    </CustomLayoutDrawer>
  );
};
